import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { MapPin, QrCode, Ticket } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";

import { guestRewardsApi } from "@/api/guest/guestRewardsApi.js";
import GuestLayout from "@/components/guest/GuestLayout.jsx";
import { Button } from "@/components/ui/button";
import { useSession } from "@/hooks/useSession.js";
import {
  formatGuestDate,
  getApiData,
  getApiMessage,
  read,
} from "@/utils/guestRewardsFormat.js";
import {
  FullScreenLoading,
  GuestPageShell,
  LoadingBlock,
  PageHeader,
  RegistrationPrompt,
} from "@/pages/guest/GuestProfilePage.jsx";

const GuestCouponsPage = () => {
  const navigate = useNavigate();
  const { session, hotel, room, loading: sessionLoading } = useSession();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionLoading && !session) navigate("/guest/expired", { replace: true });
  }, [sessionLoading, session, navigate]);

  useEffect(() => {
    const loadCoupons = async () => {
      if (!session) return;
      try {
        setLoading(true);
        const response = await guestRewardsApi.getCoupons();
        setData(getApiData(response));
      } catch (error) {
        toast.error(getApiMessage(error, "Unable to load coupons."));
      } finally {
        setLoading(false);
      }
    };
    loadCoupons();
  }, [session]);

  if (sessionLoading) return <FullScreenLoading />;
  if (!session) return null;

  const eventCoupons = read(data, "eventCoupons", "EventCoupons", []);
  const requiresRegistration = read(data, "requiresRegistration", "RequiresRegistration", true);

  return (
    <>
      <Helmet>
        <title>My coupons - Guest Portal</title>
      </Helmet>
      <GuestLayout hotel={hotel} room={room} session={session}>
        <GuestPageShell onBack={() => navigate("/guest/home")}>
          <PageHeader icon={Ticket} title="My coupons" subtitle="View package event coupons for your active memberships." />
          {loading ? (
            <LoadingBlock text="Loading coupons..." />
          ) : requiresRegistration ? (
            <RegistrationPrompt onRegister={() => navigate(`/guest/register?returnTo=${encodeURIComponent("/guest/coupons")}`)} />
          ) : (
            <div className="space-y-6">
              <section>
                <h2 className="mb-3 text-lg font-bold text-slate-900">Package event coupons</h2>
                {eventCoupons.length === 0 ? (
                  <EmptyState text="No package event coupons available right now." />
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {eventCoupons.map((coupon) => (
                      <EventCouponCard key={read(coupon, "id", "Id")} coupon={coupon} />
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}
        </GuestPageShell>
      </GuestLayout>
    </>
  );
};

const EventCouponCard = ({ coupon }) => {
  const packageEvent = read(coupon, "packageEvent", "PackageEvent", {});
  const qrValue = read(coupon, "qrDisplayValue", "QrDisplayValue", "");
  return (
    <article className="rounded-2xl border bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-bold text-slate-900">{read(packageEvent, "title", "Title", "Package event")}</p>
          <p className="mt-1 flex items-center gap-1 text-sm text-slate-500">
            <MapPin className="h-3.5 w-3.5" />
            {read(packageEvent, "locationName", "LocationName", "Location not set")}
          </p>
        </div>
        <span className="rounded-full border px-3 py-1 text-xs font-medium">
          {formatEventCouponStatus(read(coupon, "status", "Status"))}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-[112px_minmax(0,1fr)] gap-3">
        <div className="flex h-28 w-28 items-center justify-center rounded-xl border bg-white">
          {qrValue ? (
            <QRCodeSVG value={qrValue} size={88} />
          ) : (
            <QrCode className="h-8 w-8 text-slate-400" />
          )}
        </div>
        <div className="min-w-0 text-xs text-slate-500">
          <p className="font-semibold text-slate-900">{read(coupon, "couponCode", "CouponCode", "Event coupon")}</p>
          <p className="mt-2">Remaining: {read(coupon, "usageRemaining", "UsageRemaining", 0)}</p>
          <p>Valid to: {formatGuestDate(read(coupon, "validTo", "ValidTo"))}</p>
          <p className="mt-2 break-all rounded-lg bg-slate-50 p-2">{qrValue || "QR token not available"}</p>
        </div>
      </div>
      <Button type="button" variant="outline" className="mt-3 w-full" onClick={() => window.print()}>
        Print coupon
      </Button>
    </article>
  );
};

const formatEventCouponStatus = (value) => {
  const normalized = Number(value);
  if (normalized === 2) return "Pending approval";
  if (normalized === 3) return "Approved";
  if (normalized === 4) return "Used";
  if (normalized === 5) return "Expired";
  if (normalized === 6) return "Cancelled";
  return "Available";
};

const EmptyState = ({ text }) => (
  <div className="rounded-2xl border border-dashed p-6 text-center text-sm text-slate-600">{text}</div>
);

export default GuestCouponsPage;
